'use client';

/** Configurable empty state with variants for search results, file lists and general empty states, plus an optional action button. Built on StateMessageBase. */

import { colors } from '../css-refs.colors.ts';
import { spacing } from '../css-refs.spacing.ts';
import { getCircularContainerStyle } from '../common/badge-styles.ts';
import { ARIA_ROLES } from '../common/aria-tokens.ts';
import { SemanticIcon } from '../icons/SemanticIcon.tsx';
import { StateMessageBase } from './StateMessageBase.tsx';
import type { EmptyStateVariant, EmptyStateProps } from '@federa/types/ui-empty-state';
import type { SemanticIconName } from '@federa/types/ui-icons';

const DEFAULT_EMPTY_STATE_ICON: SemanticIconName = 'info';

const VARIANT_ICON_MAP: Record<EmptyStateVariant, SemanticIconName> = {
	default: DEFAULT_EMPTY_STATE_ICON,
	search: 'search',
	error: 'alert',
	file: 'document',
};

export function EmptyState({
	variant = 'default',
	icon,
	title,
	description,
	action,
	secondaryAction,
	size = 'md',
	className = '',
	titleTag,
}: EmptyStateProps) {
	// THREE answers, not two: `undefined` keeps the variant default, an element replaces it, and `null` -- React's own
	// spelling of "render nothing" -- removes it. `icon ?? default` read null as absent, so a glyph could be swapped but
	// never dropped, and every empty state carried one whether or not it said anything.
	const resolvedIcon = icon !== undefined ? icon : (
		<div
			style={{
				...getCircularContainerStyle(spacing[16], colors.background.tertiary),
				marginBottom: spacing[4],
			}}
		>
			<SemanticIcon name={VARIANT_ICON_MAP[variant]} size="lg" color="secondary" />
		</div>
	);

	return (
		<StateMessageBase
			title={title}
			description={description}
			action={action}
			secondaryAction={secondaryAction}
			iconElement={resolvedIcon}
			className={className}
			role={ARIA_ROLES.status}
			size={size}
			titleTag={titleTag}
		/>
	);
}

export default EmptyState;
